import { useEffect, useRef, useState } from "react";
import {
  FileText,
  Info,
  List,
  Loader2,
  Pencil,
  Settings2,
  UploadCloud,
  X,
  type LucideIcon,
} from "lucide-react";
import { toast } from "../../lib/toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import { AgentExtractedPartRow } from "../../../context/AgentExtractedDataContext";
import { handleAuthExpired } from "../auth/auth";
const API_BASE =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL || ""
    : process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

type ClassificationTab = "document-upload" | "extracted-parameters";

interface RequestDetailField {
  label: string;
  value: string;
}

interface ExtractedPartRow {
  part_number: string;
  part_name: string;
  description: string;
  process: string;
  geo: string;
  quantity: string | number;
}

interface CMDocFile {
  key: string;
  icon: LucideIcon;
  type: string;
  name: string;
  meta: string;
  optional?: boolean;
}

const CM_DOC_FILES: CMDocFile[] = [
  { key: "drawing", icon: FileText, type: "2D Drawing (PDF/DWG)", name: "EVH-4420-A3_rev-C.pdf", meta: "18 dimensions • 4 GD&T callouts" }
];

const buildInitialProgressMap = (): Record<string, number> =>
  CM_DOC_FILES.reduce<Record<string, number>>((acc, file) => {
    acc[file.key] = file.optional ? -1 : 0;
    return acc;
  }, {});

function CMFileCard({
  file,
  displayName,
  progress,
  uploading,
}: {
  file: CMDocFile;
  displayName: string;
  progress: number;
  uploading: boolean;
}) {
  const complete = progress === 100;
  const skipped = progress === -1;
  const Icon = file.icon;

  if (skipped) {
    return (
      <div className="cm-file-card cm-file-card--skipped">
        <div className="cm-file-card__row">
          <Icon size={16} aria-hidden="true" />
          <p className="cm-file-card__type">{file.type}</p>
        </div>
        <p className="cm-file-card__name cm-file-card__name--warn">not provided</p>
        <p className="cm-file-card__meta cm-file-card__meta--warn">{file.meta}</p>
      </div>
    );
  }

  const pct = Math.round(Math.max(0, Math.min(100, progress)));

  return (
    <div className={`cm-file-card${complete ? " cm-file-card--complete" : ""}`}>
      <div className="cm-file-card__row">
        <Icon size={16} aria-hidden="true" />
        <p className="cm-file-card__type">{file.type}</p>
      </div>
      <div className="flex justify-between">
        <p className="cm-file-card__name">{displayName}</p>
        <p className="cm-file-card__pct">{pct}%</p>
      </div>
      <progress className="cm-file-card__progress" value={pct} max={100} aria-label={`${file.type} upload progress`} />
      <div className="cm-file-card__status-row">
        <p className={`cm-file-card__status ${complete ? "cm-file-card__hint-row--ok" : uploading ? "cm-file-card__hint-row--uploading" : ""}`}>{complete ? "Upload complete" : uploading ? "Uploading" : "Queued"}</p>
      </div>
      {complete ? (
        <div className="cm-file-card__hint-row cm-file-card__hint-row--meta">
          <Settings2 size={11} aria-hidden="true" />
          <p>{file.meta}</p>
        </div>
      ) : null}
    </div>
  );
}

const buildRequestDetails = (data: any): RequestDetailField[] => {
  const metadata = data?.metadata ?? {};
  const summary = data?.summary ?? {};

  const confidence =
    typeof metadata.extraction_confidence === "number"
      ? `${Math.round(metadata.extraction_confidence * 100)}%`
      : "—";
  const partNumbers =
    Array.isArray(metadata.part_numbers) && metadata.part_numbers.length
      ? metadata.part_numbers.join(", ")
      : "None listed";
  const quantities =
    Array.isArray(metadata.quantities) && metadata.quantities.length
      ? metadata.quantities.join(", ")
      : "None listed";
  const status =
    typeof data?.status === "string" && data.status.length
      ? data.status.charAt(0).toUpperCase() + data.status.slice(1)
      : "—";

  return [
    { label: "BKM Number", value: metadata.bkm_number || "—" },
    { label: "Document Title", value: metadata.document_title || "—" },
    { label: "Revision", value: metadata.revision_number || "—" },
    { label: "Revision Date", value: metadata.revision_date || "—" },
    { label: "Source Page", value: metadata.source_page != null ? `Page ${metadata.source_page}` : "—" },
    { label: "Extraction Confidence", value: confidence },
    { label: "Parts Extracted", value: String(summary.parts_count ?? 0) },
    { label: "Steps Extracted", value: String(summary.steps_count ?? 0) },
    { label: "Part Numbers", value: partNumbers },
    { label: "Quantities", value: quantities },
    { label: "Status", value: status },
  ];
};

const CLASSIFICATION_TABS: { id: ClassificationTab; label: string }[] = [
  { id: "document-upload", label: "Document Upload" },
  { id: "extracted-parameters", label: "Extracted Parameters" },
];

interface Props {
  onConfirmed: () => void;
  setAgentExtractedRows: React.Dispatch<React.SetStateAction<AgentExtractedPartRow[]>>;
  onWorkflowReset?: () => void;
}

export function ClassificationStep({ onConfirmed, setAgentExtractedRows, onWorkflowReset }: Props) {
  const [activeTab, setActiveTab] = useState<ClassificationTab>("document-upload");
  const [requestAccordionValue, setRequestAccordionValue] = useState<string | undefined>(undefined);
  const [isDocumentUploaded, setIsDocumentUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showFileCards, setShowFileCards] = useState(false);
  const [uploadedDrawingFileName, setUploadedDrawingFileName] = useState("");
  const [fileProgressMap, setFileProgressMap] = useState<Record<string, number>>(buildInitialProgressMap());
  const [requestDetails, setRequestDetails] = useState<RequestDetailField[]>([]);
  const [draftDetails, setDraftDetails] = useState<RequestDetailField[]>([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [extractedPartRows, setExtractedPartRows] = useState<ExtractedPartRow[]>([]);
  const [extractedPage, setExtractedPage] = useState(1);
  const [extractedRowsPerPage, setExtractedRowsPerPage] = useState(10);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setExtractedPage(1);
  }, [extractedPartRows.length]);

  useEffect(() => {
    setExtractedPage(1);
  }, [extractedRowsPerPage]);

  const stopProgressSimulation = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startProgressSimulation = () => {
    stopProgressSimulation();

    setFileProgressMap((prev) => ({
      ...prev,
      drawing: Math.max(prev.drawing, 18),
      model: Math.max(prev.model, 12),
      bom: Math.max(prev.bom, 10),
      spec: Math.max(prev.spec, 8),
      template: Math.max(prev.template, 6),
      cert: -1,
    }));

    progressIntervalRef.current = setInterval(() => {
      setFileProgressMap((prev) => {
        const next = { ...prev };
        ["drawing", "model", "bom", "spec", "template"].forEach((key) => {
          const current = next[key] ?? 0;
          if (current < 92) {
            next[key] = Math.min(92, current + 6);
          }
        });
        next.cert = -1;
        return next;
      });
    }, 250);
  };

  const resetForNewUpload = () => {
    onWorkflowReset?.();
    stopProgressSimulation();
    setActiveTab("document-upload");
    setIsDocumentUploaded(false);
    setRequestAccordionValue(undefined);
    setRequestDetails([]);
    setDraftDetails([]);
    setExtractedPartRows([]);
    setAgentExtractedRows([]);
    setExtractedPage(1);
    setIsEditOpen(false);
    setFileProgressMap(buildInitialProgressMap());
  };

  const handleFileSelected = async (file: globalThis.File | undefined) => {
    if (!file || isUploading) return;

    resetForNewUpload();
    setShowFileCards(true);
    setUploadedDrawingFileName(file.name);
    setIsUploading(true);
    startProgressSimulation();

    try {
      const bkmNumberGuess = file.name.replace(/\.[^./]+$/, "").trim();

      if (!bkmNumberGuess) {
        toast.error("Could not determine a BKM number from the file name.");
        return;
      }

      const lookupResponse = await fetch(
        `${API_BASE}/api/cost-model/lookup/${encodeURIComponent(bkmNumberGuess)}`,
        { credentials: "include" }
      );

      if (lookupResponse.status === 404) {
        toast.error(`No extracted data found for BKM ${bkmNumberGuess}.`);
        return;
      }

      if (lookupResponse.status === 401) {
        handleAuthExpired();
        return;
      }

      if (!lookupResponse.ok) {
        const errorBody = await lookupResponse.json().catch(() => ({}));
        throw new Error(errorBody?.error || `Lookup failed with status ${lookupResponse.status}`);
      }

      const lookup = await lookupResponse.json();
      const extractedRows: ExtractedPartRow[] = Array.isArray(lookup.rows)
        ? lookup.rows.map((row: any) => ({
            ...row,
            geo:
              typeof row?.geo === "string" && row.geo.trim() && row.geo.trim() !== "—"
                ? row.geo
                : "US",
          }))
        : [];
      setRequestDetails(buildRequestDetails(lookup.data));
      setExtractedPartRows(extractedRows);
      setAgentExtractedRows(extractedRows);
      stopProgressSimulation();
      setFileProgressMap({
        drawing: 100,
        model: 100,
        bom: 100,
        spec: 100,
        template: 100,
        cert: -1,
      });
      setIsDocumentUploaded(true);
      setRequestAccordionValue("request-details");

      toast.success(`Found existing extraction for BKM ${lookup.bkm_number} — showing saved data.`);
    } catch (err) {
      console.error("BKM lookup failed", err);
      stopProgressSimulation();
      setFileProgressMap(buildInitialProgressMap());
      setShowFileCards(false);
      toast.error("Could not process the document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectSupplierQuote = () => {
    toast.success("Supplier quote uploaded — parameters extracted.");
  };

  const handleConfirmInputs = () => {
    onConfirmed();
  };

  const openEditDrawer = () => {
    setDraftDetails(requestDetails);
    setIsEditOpen(true);
  };

  const closeEditDrawer = () => setIsEditOpen(false);

  const updateDraftValue = (index: number, value: string) => {
    setDraftDetails((prev) => prev.map((field, i) => (i === index ? { ...field, value } : field)));
  };

  const handleSaveDetails = () => {
    setRequestDetails(draftDetails);
    setIsEditOpen(false);
    toast.success("Request details updated.");
  };

  const hasExtractedData = extractedPartRows.length > 0;
  const extractedTotalPages = Math.max(1, Math.ceil(extractedPartRows.length / extractedRowsPerPage));
  const safeExtractedPage = Math.min(extractedPage, extractedTotalPages);
  const extractedPageRows = extractedPartRows.slice(
    (safeExtractedPage - 1) * extractedRowsPerPage,
    safeExtractedPage * extractedRowsPerPage
  );

  const continueConfig =
    activeTab === "document-upload"
      ? {
          onClick: () => {
            setActiveTab("extracted-parameters");
            window.scrollTo({ top: 0, behavior: "instant" });
          },
          label: "Continue →",
          disabled: !isDocumentUploaded,
          disabledMessage: "Please upload the file",
        }
      : {
          onClick: () => {
            handleConfirmInputs();
            window.scrollTo({ top: 0, behavior: "instant" });
          },
          label: "Confirm & Generate Cost Model →",
          disabled: !hasExtractedData,
          disabledMessage: "No extracted parameters to confirm yet",
        };

  return (
    <>
      {/* Tab bar */}
      <div className="dash-tabs-cost-model-flow justify-between" role="tablist" aria-label="Classification sections">
        <span className="dash-tab-placeholder flex items-center gap-2">
          {CLASSIFICATION_TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            className={`dash-tab${activeTab === id ? " is-active" : ""}`}
            onClick={() => setActiveTab(id)}
          >
              {label}
            </button>
          ))}
        </span>
        <button
          type="button"
          className={`cmw-sticky-continue__btn${continueConfig.disabled ? " is-disabled" : ""}`}
          aria-disabled={continueConfig.disabled}
          onClick={() => {
            if (continueConfig.disabled) {
              toast.error(continueConfig.disabledMessage);
              return;
            }
            continueConfig.onClick();
          }}>
          {continueConfig.label}
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "extracted-parameters" ? (
        <div className="cmw-params">
          <div className="cmw-params__header">
            <div className="cmw-params__heading">
              <h3 className="cmw-params__title">Extracted Parameters</h3>
              <span className="badge badge--purple cmw-params__count">25 Parameters Extracted</span>
            </div>
          </div>
          <div className="cmw-review">
            <div className="cmw-review__badge">
              <span>AI</span> Extraction Review
            </div>
            <p className="cmw-review__text">
              3 parameters fall below the 85% confidence threshold — <strong>cycle time (84%)</strong>,{" "}
              <strong>logistics basis (82%)</strong> and <strong>critical tolerance (86%)</strong>. Cycle time
              was inferred from feature recognition on the STEP model rather than a stated routing; confirming
              it changes the machining cost by roughly ±$0.45/pc. Everything else is fully supported by the
              drawing and BOM.
            </p>
          </div>
          <div className="cmw-params__notice">
            <Info size={16} aria-hidden="true" />
            <span>
              Every value below was read from the uploaded documents. Low-confidence values are flagged — click
              Edit on any block to correct them in the side-editor.
            </span>
          </div>

          {extractedPartRows.length > 0 && (
            <>
              <div className="cmw-param-card">
                <div className="cmw-param-card__header">
                  <div className="cmw-param-card__title">
                    <List size={15} aria-hidden="true" />
                    Part &amp; Process Details
                  </div>
                </div>
                <table className="cmw-param-table">
                  <thead>
                    <tr>
                      <th>BKM Name</th>
                      <th>Part Number</th>
                      <th>Description</th>
                      <th>Process</th>
                      <th>Geo</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extractedPageRows.map((row, index) => (
                      <tr key={`${row.part_number}-${row.process}-${index}`}>
                        <td>{row.part_name}</td>
                        <td>{row.part_number}</td>
                        <td>{row.description}</td>
                        <td>{row.process}</td>
                        <td>{row.geo}</td>
                        <td>{row.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="cmw-param-card__pagination">
                {extractedTotalPages > 1 && (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            setExtractedPage((page) => Math.max(1, page - 1));
                          }}
                          aria-disabled={safeExtractedPage === 1}
                          tabIndex={safeExtractedPage === 1 ? -1 : 0}
                        />
                      </PaginationItem>

                      {Array.from({ length: extractedTotalPages }, (_, index) => index + 1).map((pageNumber) => (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            href="#"
                            isActive={pageNumber === safeExtractedPage}
                            onClick={(event) => {
                              event.preventDefault();
                              setExtractedPage(pageNumber);
                            }}
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            setExtractedPage((page) => Math.min(extractedTotalPages, page + 1));
                          }}
                          aria-disabled={safeExtractedPage === extractedTotalPages}
                          tabIndex={safeExtractedPage === extractedTotalPages ? -1 : 0}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
                <div className="cmw-param-card__pagination-size">
                  <label htmlFor="classification-rows-per-page">Rows per page</label>
                  <select
                    id="classification-rows-per-page"
                    value={extractedRowsPerPage}
                    onChange={(event) => setExtractedRowsPerPage(Number(event.target.value))}
                  >
                    {[10,20,50,100].map((count) => (
                      <option key={count} value={count}>
                        {count}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {activeTab === "document-upload" ? (
            <Accordion type="single" collapsible defaultValue="document-upload" className="cmw-upload-accordion">
              <AccordionItem value="document-upload" className="cmw-upload-accordion__item cmw-panel">
                <AccordionTrigger className="cmw-upload-accordion__trigger">
                  <span className="cmw-upload-accordion__title-wrap">
                    <span className="cmw-upload-accordion__title">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-chevron-down text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200"><path d="m6 9 6 6 6-6"></path></svg>
                      BOM Document
                    </span>
                  </span>
                  <span className="badge badge--purple cmw-upload-accordion__badge">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" /><path d="M6.453 15h11.094" /><path d="M8.5 2h7" /></svg>
                    AI PARSING
                  </span>
                </AccordionTrigger>
                <AccordionContent className="cmw-upload-accordion__content">
                  <div className={`file-upload-zone cmw-upload-zone${isUploading ? " is-uploading" : ""}`}>
                    {isUploading ? (
                      <>
                        <div className="cmw-upload-zone__icon cmw-upload-zone__icon--spin" aria-hidden="true">
                          <Loader2 size={18} />
                        </div>
                        <div className="cmw-upload-zone__title">Uploading &amp; analyzing document…</div>
                        <div className="cmw-upload-zone__hint">This can take a moment for larger PDFs.</div>
                      </>
                    ) : (
                      <>
                        <div className="cmw-upload-zone__icon" aria-hidden="true">
                          <UploadCloud size={18} />
                        </div>
                        <div className="cmw-upload-zone__title">
                          Drawings · 3D models · BOM · Technical specs · Input template
                        </div>
                        <div className="cmw-upload-zone__hint">Click to upload a document for extraction</div>
                      </>
                    )}
                    <input
                      type="file"
                      className="cmw-upload-zone__input"
                      accept=".pdf"
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleFileSelected(file);
                        e.target.value = "";
                      }}
                      aria-label="Upload document for cost model extraction"
                    />
                  </div>

                  {showFileCards || isDocumentUploaded ? (
                    <div className="cmw-file-cards-wrap">
                      <div className="cmw-file-cards">
                        {CM_DOC_FILES.map((file) => (
                          <CMFileCard
                            key={file.key}
                            file={file}
                            displayName={file.key === "drawing" && uploadedDrawingFileName ? uploadedDrawingFileName : file.name}
                            progress={fileProgressMap[file.key] ?? (file.optional ? -1 : 0)}
                            uploading={isUploading && (fileProgressMap[file.key] ?? 0) >= 0 && (fileProgressMap[file.key] ?? 0) < 100}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <Accordion
                    type="single"
                    collapsible
                    value={requestAccordionValue}
                    onValueChange={(value) => setRequestAccordionValue(value || undefined)}
                    className="cmw-request-accordion edit-details"
                  >
                    <AccordionItem value="request-details" className="cmw-request-accordion__item">
                      <AccordionTrigger
                        className={`cmw-request-accordion__trigger${!isDocumentUploaded ? " is-disabled" : ""}`}
                        disabled={!isDocumentUploaded}
                      >
                        <span className="cmw-request-accordion__title-wrap">
                          <span className="cmw-request-accordion__title">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-chevron-down text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200"><path d="m6 9 6 6 6-6"></path></svg>
                            Request Details
                          </span>
                        </span>
                        <button
                          type="button"
                          className="cmw-details__edit"
                          disabled={!isDocumentUploaded}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDocumentUploaded) return;
                            openEditDrawer();
                          }}
                        >
                          <Pencil size={12} aria-hidden="true" />
                          Edit details
                        </button>
                      </AccordionTrigger>
                      <AccordionContent className="cmw-request-accordion__content">
                        {isDocumentUploaded ? (
                          <div className="cmw-details">
                            <div className="cmw-details__grid">
                              {requestDetails.map((field) => (
                                <div className="cmw-details__col" key={field.label}>
                                  <div className="cmw-details__label">{field.label}</div>
                                  <div className="cmw-details__value" title={field.value}>{field.value}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="cmw-panel__placeholder">Upload a document to review the extracted request details.</div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : null}
        </>
      )}

      {/* Sticky continue action */}
      {/* {continueConfig && (
        <>
          <div className="cmw-sticky-continue-spacer" aria-hidden="true" />
          <div className="cmw-sticky-continue">
            <button
              type="button"
              className="cmw-sticky-continue__btn"
              onClick={continueConfig.onClick}
            >
              {continueConfig.label}
            </button>
          </div>
        </>
      )} */}

      {/* Edit drawer */}
      {isEditOpen && (
        <div className="cmw-edit-overlay" onClick={closeEditDrawer}>
          <div className="cmw-edit-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="cmw-edit-drawer__header">
              <h3 className="cmw-edit-drawer__title">Edit request details</h3>
              <button
                type="button"
                className="cmw-edit-drawer__close"
                onClick={closeEditDrawer}
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
            <div className="cmw-edit-drawer__body">
              {draftDetails.map((field, index) => (
                <div key={field.label} className="cmw-edit-drawer__field">
                  <div className="cmw-details__label">{field.label}</div>
                  <input
                    type="text"
                    className="cmw-edit-drawer__input"
                    value={field.value}
                    onChange={(e) => updateDraftValue(index, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="cmw-edit-drawer__footer">
              <button type="button" className="btn btn--outline" onClick={closeEditDrawer}>
                Cancel
              </button>
              <button type="button" className="btn btn--primary" onClick={handleSaveDetails}>
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
